import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import Layout from '../../../components/Layout';
import TeacherNavbar from '../../../components/TeacherNavbar/TeacherNavbar';
import ReviewCard from '../../../components/Dashboard/PendingRepliesModal/ReviewCard';
import { axiosInstance } from '../../../utils/tokenUtils';
import { format, parseISO, addMinutes } from 'date-fns';

const mapSessionType = type => {
  const typeMap = {
    one_on_one: 'One-On-One',
    group: 'Group Session',
    prime: 'Prime',
  };
  return typeMap[type] || type;
};

const mapSchoolLevel = level => {
  const levelMap = {
    elementary: 'Elementary School',
    middle_school: 'Middle School',
    high_school: 'High School',
    college: 'College',
    university: 'University',
  };
  return levelMap[level] || level;
};

const transformReviewData = apiReview => {
  const startTime = parseISO(apiReview.session.start_time);
  const endTime = addMinutes(startTime, apiReview.session.length_in_minutes);

  return {
    id: apiReview.id,
    learner: {
      name: `${apiReview.student_profile.first_name} ${apiReview.student_profile.last_name}`,
      profile_image: apiReview.student_profile.profile_image || '',
      school_level: mapSchoolLevel(apiReview.session.level_of_teaching),
    },
    session: {
      subject: apiReview.session.subject,
      date: format(startTime, 'EEE d MMM, yyyy'),
      timing: `${format(startTime, 'h:mm a')} - ${format(endTime, 'h:mm a')}`,
      type: mapSessionType(apiReview.session.session_type),
    },
    rating: apiReview.rating,
    review_text: apiReview.feedback,
    teacher_response: apiReview.teacher_response,
    responded_at: apiReview.responded_at,
    is_hidden: apiReview.is_hidden ?? false,
  };
};

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [instructorProfile, setInstructorProfile] = useState(null);

  const fetchInstructorProfile = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/auth/users/me');
      setInstructorProfile({
        name: `${response.data.first_name} ${response.data.last_name}`,
        image: response.data.profile_image || '',
      });
    } catch (err) {
      console.error('Error fetching instructor profile:', err);
    }
  }, []);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('/teachers/reviews', {
        params: { offset: 0, limit: 50 },
      });
      const transformedReviews = response.data.results.map(transformReviewData);
      setReviews(transformedReviews);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      if (err?.response?.status === 404) {
        setError('No reviews found');
      } else {
        setError('Failed to load reviews. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInstructorProfile();
    fetchReviews();
  }, [fetchInstructorProfile, fetchReviews]);

  const handleRefresh = () => {
    fetchReviews();
  };

  const handleHideSuccess = (reviewId, newIsHidden) => {
    setReviews(prev => prev.map(r => (r.id === reviewId ? { ...r, is_hidden: newIsHidden } : r)));
  };

  return (
    <Layout title="Klass Online | Reviews">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <TeacherNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <Box className="flex flex-col p-[1rem] gap-3">
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#14171f' }}>
              Reviews
            </Typography>

            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Typography color="error" sx={{ py: 4 }}>
                {error}
              </Typography>
            ) : reviews.length === 0 ? (
              <Typography sx={{ py: 4, color: '#56585e' }}>No reviews yet.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {reviews.map(review => (
                  <ReviewCard
                    key={review.id}
                    review={review}
                    onReplySuccess={handleRefresh}
                    onHideSuccess={handleHideSuccess}
                    instructorProfile={instructorProfile}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default Reviews;
